/**
 * Auth Store - Zustand with localStorage persistence.
 *
 * Security model:
 *   - Access token: persisted in localStorage (survives page refresh).
 *   - Refresh token: httpOnly cookie managed by auth-service, invisible to JS.
 *   - User info: persisted in localStorage.
 *   - On logout: clear localStorage, memory, AND revoke server token.
 *
 * Persistent session features:
 *   - Token expiry tracking with proactive refresh
 *   - Automatic session restoration on app startup
 *   - Remember Me support (OPT-IN; ~3-day / 72h sliding session window)
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';
import { createRememberStorage, getRemember } from '../lib/rememberStorage';
import { resolveApiBase } from "../lib/apiBase";

/**
 * How long a remembered session may live since the last successful auth/refresh
 * before we force a fresh sign-in. SLIDING: every login/refresh resets the clock,
 * so an actively-used session never drops mid-work, but an idle or closed one
 * lapses after this window (about three days). Keep in sync with the backend
 * refresh-cookie max-age in app/connectors/engine.py. The login screen no longer
 * prints the exact duration, so this can be tuned freely.
 */
/**
 * How long a remembered session survives without being used.
 *
 * Was 72 hours, which meant a person who used Resumark on Monday and came back
 * on Friday had to sign in again - for no security gain anybody asked for. The
 * window SLIDES: every refresh pushes it forward, so this is "idle for 30 days"
 * rather than "30 days since you signed in". An active user is never signed
 * out, and an abandoned browser still lapses.
 *
 * Kept in sync with the refresh cookie's max-age in app/connectors/engine.py -
 * the cookie is the server half of this same window, and if the two disagree
 * the shorter one silently wins.
 */
export const REMEMBER_WINDOW_HOURS = 30 * 24;
const REMEMBER_WINDOW_MS = REMEMBER_WINDOW_HOURS * 60 * 60 * 1000;

/**
 * True when a remembered session has gone past the sliding window since its last
 * login/refresh and must re-authenticate. Pure + injectable clock so it's unit-
 * testable; used by the rehydration cap below.
 */
export function isRememberWindowExpired(
  lastAuthAt: number | null | undefined,
  now: number = Date.now(),
): boolean {
  return lastAuthAt != null && now - lastAuthAt > REMEMBER_WINDOW_MS;
}

// Relative on the web (Vite proxy in dev, Cloudflare rewrite in prod),
// absolute in the native shell. See lib/apiBase.ts for why.
const API_BASE = resolveApiBase();

interface User {
  id: string;
  name: string;
  email: string;
  /** OAuth photo (https URL), captured from Google/GitHub at sign-in. */
  avatar_url?: string;
  /** Custom display picture (data: URI) uploaded by the user; persisted
   *  server-side in users.avatar_url and hydrated by AppShell on boot. */
  custom_avatar?: string;
  onboarding_completed: boolean;
}

interface AuthState {
  accessToken: string | null;
  tokenExpiresAt: number | null; // Unix timestamp (ms) when access token expires
  /** Unix ms of the last successful login/refresh - drives the sliding
   *  REMEMBER_WINDOW_HOURS session cap enforced on app startup. */
  lastAuthAt: number | null;
  user: User | null;
  isAuthenticated: boolean;
  isRefreshing: boolean;
  rememberMe: boolean; // Persist session across browser restarts

  // Actions
  setAccessToken: (token: string, expiresIn?: number) => void;
  setUser: (user: User) => void;
  login: (accessToken: string, user: User, expiresIn?: number, rememberMe?: boolean) => void;
  logout: () => void;
  logoutAsync: () => Promise<void>;
  completeOnboarding: () => void;
  setRememberMe: (value: boolean) => void;
  
  // Session management
  refreshSession: () => Promise<boolean>;
  isTokenExpired: () => boolean;
  getTimeUntilExpiry: () => number;
}

// Refresh timer reference (module-level to persist across renders).
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

// Module-level flag for in-flight refresh dedupe. The previous version
// read `state.isRefreshing` from a closure captured BEFORE `await`, so
// two parallel callers both saw false and both POSTed to /v1/auth/refresh.
// Reading at module scope makes the check observable across callers
// without relying on the (deferred) zustand state mutation.
let isRefreshInFlight = false;

/**
 * Schedule proactive token refresh 1 minute before expiry.
 * This prevents 401 errors and ensures seamless user experience.
 */
function scheduleProactiveRefresh(expiresAt: number, refreshFn: () => Promise<boolean>) {
  // Clear any existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  const now = Date.now();
  const refreshAt = expiresAt - 60_000; // 1 minute before expiry
  const delay = refreshAt - now;

  if (delay > 0) {
    refreshTimer = setTimeout(async () => {
      console.log('[Auth] Proactive token refresh triggered');
      await refreshFn();
    }, delay);
    console.log(`[Auth] Scheduled refresh in ${Math.round(delay / 1000)}s`);
  }
}



export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      tokenExpiresAt: null,
      lastAuthAt: null,
      user: null,
      isAuthenticated: false,
      isRefreshing: false,
      // Seed from the standalone flag (NOT the persisted blob): the flag in
      // localStorage is the single durable source of truth, re-asserted in
      // `merge` on every rehydrate so a stale value in an old blob can't override
      // it. Defaults to OFF - "keep me signed in" is OPT-IN: the user must flip it
      // on themselves (no pre-checked consent). See lib/rememberStorage.
      rememberMe: getRemember(),

      setAccessToken: (token, expiresIn) => {
        const expiresAt = expiresIn 
          ? Date.now() + expiresIn * 1000 
          : Date.now() + 15 * 60 * 1000; // Default 15 min

        // Touch lastAuthAt - a fresh token slides the remember-window forward.
        set({ accessToken: token, tokenExpiresAt: expiresAt, lastAuthAt: Date.now(), isAuthenticated: true });

        // Schedule proactive refresh
        scheduleProactiveRefresh(expiresAt, get().refreshSession);
      },

      setUser: (user) => set({ user }),

      login: (accessToken, user, expiresIn, rememberMe) => {
        const expiresAt = expiresIn 
          ? Date.now() + expiresIn * 1000 
          : Date.now() + 15 * 60 * 1000;

        set({
          accessToken,
          tokenExpiresAt: expiresAt,
          lastAuthAt: Date.now(),
          user,
          isAuthenticated: true,
          rememberMe: rememberMe ?? get().rememberMe,
        });

        // Schedule proactive refresh
        scheduleProactiveRefresh(expiresAt, get().refreshSession);
      },

      logout: () => {
        // Clear refresh timer
        if (refreshTimer) {
          clearTimeout(refreshTimer);
          refreshTimer = null;
        }
        set({
          accessToken: null,
          tokenExpiresAt: null,
          lastAuthAt: null,
          user: null,
          isAuthenticated: false
        });
      },

      /**
       * Async logout that properly revokes server token.
       * This should ALWAYS be used for user-initiated logout.
       */
      logoutAsync: async () => {
        // Capture the access token BEFORE clearing local state - the server
        // needs the bearer to revoke the Supabase session (the cookie clear
        // works without it).
        const token = get().accessToken;
        // Clear local state IMMEDIATELY so sign-out feels instant and the
        // redirect lands logged-out - never make the user wait on the network
        // (the backend can cold-start for tens of seconds).
        get().logout();
        // Revoke server-side in the BACKGROUND (best-effort, never blocks the
        // UI): DELETE /v1/auth/logout clears the httpOnly refresh cookie so this
        // browser can't silently refresh back in, and - with the bearer -
        // revokes the Supabase session. A plain client-only logout left the
        // refresh cookie alive, which is the gap this closes.
        try {
          await axios.delete(`${API_BASE}/v1/auth/logout`, {
            withCredentials: true,
            timeout: 10_000,
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          console.log('[Auth] Server session revoked');
        } catch (error) {
          // Cookie is cleared by the server regardless; log and move on.
          console.warn('[Auth] Failed to revoke server token:', error);
        }
      },

      completeOnboarding: () =>
        set((state) => ({
          user: state.user ? { ...state.user, onboarding_completed: true } : null,
        })),

      setRememberMe: (value) => {
        // In-memory ONLY. We deliberately do NOT write the durable flag here:
        // "keep me signed in" is committed (made persistent) solely when a
        // sign-in actually succeeds - see commitPendingRemember, called from the
        // Google credential + OAuth-callback success paths. So toggling it ON and
        // then leaving WITHOUT signing in never sticks; on the next load it is
        // back OFF. The choice is staged for the in-flight OAuth round-trip at
        // provider-click time (setPendingRemember in LoginPage).
        set({ rememberMe: value });
      },

      /**
       * Attempt to refresh the session using httpOnly refresh cookie.
       * Returns true if successful, false if refresh failed.
       */
      refreshSession: async () => {
        // Dedupe via module-level flag - set synchronously BEFORE any
        // await, so two parallel callers see the second as in-flight.
        if (isRefreshInFlight) {
          console.log('[Auth] Refresh already in progress, skipping');
          return false;
        }
        isRefreshInFlight = true;
        set({ isRefreshing: true });

        try {
          const { data } = await axios.post(
            `${API_BASE}/v1/auth/refresh`,
            // Tell the server the persistence choice so it matches the refresh
            // cookie's lifetime to it: a session cookie (dies on browser close)
            // when not remembered, the 30-day sliding cookie when remembered.
            { remember: get().rememberMe },
            { withCredentials: true, timeout: 15_000 }
          );

          const newToken = data.access_token;
          const expiresIn = data.expires_in || 900; // Default 15 min
          const expiresAt = Date.now() + expiresIn * 1000;

          set({
            accessToken: newToken,
            tokenExpiresAt: expiresAt,
            // A successful refresh slides the remember-window forward.
            lastAuthAt: Date.now(),
            isAuthenticated: true,
            isRefreshing: false,
          });

          // Schedule next refresh
          scheduleProactiveRefresh(expiresAt, get().refreshSession);

          console.log('[Auth] Session refreshed successfully');
          return true;
        } catch (error) {
          // A REFUSAL and a FAILURE TO ASK are different things, and treating
          // them the same is why deploying looked like it signed people out:
          // the backend restarts, /auth/refresh times out or 503s, and a
          // perfectly valid session was thrown away because the server was not
          // there to confirm it.
          //
          // Only a 401/403 means "this session is dead". Anything else - a
          // timeout, a network error, a 502 from a cold start - means we could
          // not reach the server, so we keep the session and try again shortly.
          const status = (error as { response?: { status?: number } })?.response?.status;
          const rejected = status === 401 || status === 403;
          if (!rejected) {
            console.warn('[Auth] Refresh could not reach the server; keeping the session.', error);
            set({ isRefreshing: false });
            // Try again soon. The access token may already be expired, in which
            // case requests 401 and the interceptor lands back here - but with
            // the session intact, so recovery is automatic once the backend is
            // up.
            window.setTimeout(() => { void get().refreshSession(); }, 5_000);
            return false;
          }
          console.warn('[Auth] Session refresh rejected:', error);
          // Always discard the dead bearer so we stop firing unauthed-but-bearer requests.
          // Preserve `user` so the login screen can show 'Welcome back, X'.
          set({
            accessToken: null,
            tokenExpiresAt: null,
            isRefreshing: false,
            isAuthenticated: false,
          });
          return false;
        } finally {
          isRefreshInFlight = false;
        }
      },

      isTokenExpired: () => {
        const { tokenExpiresAt } = get();
        if (!tokenExpiresAt) return true;
        return Date.now() >= tokenExpiresAt;
      },

      getTimeUntilExpiry: () => {
        const { tokenExpiresAt } = get();
        if (!tokenExpiresAt) return 0;
        return Math.max(0, tokenExpiresAt - Date.now());
      },
    }),
    {
      name: 'resumark-auth',
      // Route persistence by the remember-me choice: localStorage when
      // remembered (survives browser restart), sessionStorage when not (the
      // browser wipes it on close → genuine "log me out when I close the tab").
      storage: createJSONStorage(() => createRememberStorage()),
      partialize: (state) => ({
        accessToken: state.accessToken,
        tokenExpiresAt: state.tokenExpiresAt,
        lastAuthAt: state.lastAuthAt,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        // rememberMe is deliberately NOT persisted here - the standalone
        // `resumark-remember` flag (getRemember) owns it. Persisting it in this
        // blob is what let a stale `rememberMe: true` from the pre-opt-in build
        // survive and keep the switch showing ON. See `merge` below.
      }),

      // Force rememberMe from the durable flag on EVERY rehydrate, overriding
      // whatever an older blob may still contain. Without this, the default
      // shallow merge restores the persisted (stale) rememberMe and the opt-in
      // default never takes effect for returning users. Everything else merges
      // normally (persisted over initial).
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<AuthState>),
        rememberMe: getRemember(),
      }),

      /**
       * On hydration (app startup), validate token and attempt refresh if needed.
       * This enables "Remember Me" - users stay logged in across browser restarts.
       */
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('[Auth] Hydration error:', error);
          return;
        }

        if (!state) return;

        // Sliding session cap: if the last successful auth/refresh was more than
        // REMEMBER_WINDOW_HOURS ago, require a fresh sign-in. This makes the
        // "Keep me signed in" promise REAL on the client - a
        // deterministic ceiling that holds regardless of cookie or supabase-js
        // refresh timing, which would otherwise quietly keep an old session
        // alive. (Legacy sessions persisted before this shipped have no
        // lastAuthAt and are left alone until their next login stamps one.)
        if (isRememberWindowExpired(state.lastAuthAt)) {
          console.log('[Auth] Remember window elapsed - clearing session');
          state.logout();
          return;
        }

        // NOTE: we deliberately do NOT clear the session here when rememberMe is
        // false. "Session-only" is enforced by the storage TIER, not a startup
        // wipe: an un-remembered session lives in sessionStorage, so the browser
        // has already discarded it on close (and on cold start there is simply
        // nothing to rehydrate). The old startup-clear also fired on an
        // in-tab refresh, logging un-remembered users out on every F5 - which
        // is wrong; sessionStorage correctly survives a refresh but not a close.

        // If we have a token, check if it's expired
        if (state.accessToken && state.tokenExpiresAt) {
          const isExpired = Date.now() >= state.tokenExpiresAt;
          
          if (isExpired) {
            console.log('[Auth] Access token expired, attempting refresh');
            // Attempt silent refresh using httpOnly cookie.
            //
            // NOTHING IS CLEARED HERE, DELIBERATELY. `refreshSession` already
            // discards the bearer when the server actually REFUSED the session
            // (401/403), and deliberately keeps it when it could not reach the
            // server at all - a cold start, a deploy, a phone that woke up on
            // no signal. This handler used to clear on any `false`, which threw
            // a perfectly good session away for exactly those cases and undid
            // the distinction refreshSession exists to make: the backend
            // sleeps, the tab opens, and a signed-in user lands on the sign-in
            // screen. `false` also means "a refresh was already in flight",
            // which is not a failure at all.
            void state.refreshSession();
          } else {
            console.log('[Auth] Valid token restored from storage');
            // Schedule proactive refresh for the restored token
            scheduleProactiveRefresh(state.tokenExpiresAt, state.refreshSession);
          }
        } else if (state.isAuthenticated) {
          // Token missing but isAuthenticated true - try to refresh
          console.log('[Auth] No token but authenticated, attempting refresh');
          state.refreshSession();
        }
      },
    }
  )
);
